import axios from 'axios';

/**
 * "Quién lo armó" (QUIEN_LO_ARMO) vive en la planilla de reclamo por siniestro
 * (GET /api/prueba/reclamo/:idSiniestro). No hay endpoint agregado, así que se consulta
 * siniestro por siniestro con un pool de concurrencia y se cachea en localStorage.
 *
 * Si el backend expone algo como GET /api/estadisticas/quienLoArmo?ids=... conviene
 * reemplazar `fetchQuienArmo` por una sola llamada y dejar el resto igual.
 */

export const ARMADORES = [
  { codigo: 'DM', nombre: 'Daniela' },
  { codigo: 'DD', nombre: 'Diego' },
  { codigo: 'OC', nombre: 'Orely' },
  { codigo: 'JG', nombre: 'Juan' },
];

const CACHE_KEY = 'dashboardQuienArmoCache';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h: el campo puede cargarse después, no cachear para siempre
const CONCURRENCY = 10;

function leerCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function guardarCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // sin espacio / modo privado: se sigue sin cache
  }
}

export function limpiarCacheQuienArmo(ids) {
  if (!ids) {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {}
    return;
  }
  const cache = leerCache();
  ids.forEach(id => delete cache[id]);
  guardarCache(cache);
}

async function fetchUno(id) {
  try {
    const { data } = await axios.get(`https://back.agsseguros.online/api/prueba/reclamo/${id}`, { timeout: 20000 });
    const v = data?.QUIEN_LO_ARMO;
    return v ? String(v).trim().toUpperCase() : null;
  } catch {
    return undefined; // error: no se cachea
  }
}

/**
 * Devuelve { [idSiniestro]: 'DM' | 'DD' | 'OC' | 'JG' | null }.
 *
 * @param {number[]} ids
 * @param {{ onProgress?: (hechos: number, total: number) => void, signal?: { cancelado: boolean }, forzar?: boolean }} opts
 */
export async function fetchQuienArmo(ids, { onProgress, signal, forzar = false } = {}) {
  const cache = leerCache();
  const ahora = Date.now();
  const resultado = {};
  const pendientes = [];

  ids.forEach(id => {
    const c = cache[id];
    if (!forzar && c && ahora - c.t < CACHE_TTL_MS) {
      resultado[id] = c.v;
    } else {
      pendientes.push(id);
    }
  });

  const total = ids.length;
  let hechos = total - pendientes.length;
  if (onProgress) onProgress(hechos, total);

  let i = 0;
  let cambios = 0;
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, pendientes.length) }, async () => {
      while (i < pendientes.length) {
        if (signal?.cancelado) return;
        const id = pendientes[i++];
        const v = await fetchUno(id);
        if (v !== undefined) {
          resultado[id] = v;
          cache[id] = { v, t: ahora };
          cambios++;
          if (cambios % 50 === 0) guardarCache(cache);
        }
        hechos++;
        if (onProgress) onProgress(hechos, total);
      }
    })
  );

  if (cambios) guardarCache(cache);
  return resultado;
}
